import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DayPilot, DayPilotScheduler } from "@daypilot/daypilot-lite-react"
import { format } from "date-fns"
import DOMPurify from "dompurify"
import { useUserStore } from "@/lib/fetch-users"
import { formatInServerTimeZone } from "@/lib/initial-time-zone"
import { joinStrings } from "@/lib/joinString"
import { getAvatarColorByRole, getRoleName } from "@/modules/common/helpers/common-helper"
import { AgentTooltipProvider } from "@/modules/shift-plan-overview/components/agent-tooltip"
import {
  getLocalizedShiftType,
  getLocalizedSpecialties,
  getShiftColors,
} from "@/modules/shift-plan-overview/helpers/shift-plan-helper"
import type { useShiftPlanOverviewQuery$data } from "@/modules/shift-plan-overview/services/__generated__/useShiftPlanOverviewQuery.graphql"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"

const MIN_SCHEDULER_HEIGHT = 200
const SCHEDULER_HEADER_HEIGHT = 36
const SCHEDULER_BOTTOM_PADDING = 16

type Agent = useShiftPlanOverviewQuery$data["shiftPlanOverview"][number]

interface ShiftTimelineProps {
  agents: readonly Agent[]
}

export const ShiftTimeline = ({ agents }: ShiftTimelineProps) => {
  const { t } = useTranslation()
  const date = useShiftPlanFiltersStore((state) => state.selectedDate)
  const users = useUserStore((state) => state.users)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [schedulerHeight, setSchedulerHeight] = useState<number>(MIN_SCHEDULER_HEIGHT)

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return
    const updateHeight = () =>
      setSchedulerHeight(
        Math.max(
          MIN_SCHEDULER_HEIGHT,
          Math.floor(wrapper.getBoundingClientRect().height) - SCHEDULER_HEADER_HEIGHT - SCHEDULER_BOTTOM_PADDING,
        ),
      )
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(wrapper)
    return () => observer.disconnect()
  }, [])

  const { agentMap, resources, events } = useMemo(() => {
    const agentMap = new Map<string, Agent>()
    const resources: { id: string; name: string }[] = []
    const events: DayPilot.EventData[] = []

    for (const agent of agents) {
      const user = users?.get(agent.userName)
      const fullName = joinStrings([user?.firstName, user?.lastName], " ")
      agentMap.set(agent.userName, agent)
      resources.push({ id: agent.userName, name: fullName })
      for (let idx = 0; idx < agent.shifts.length; idx++) {
        const shift = agent.shifts[idx]
        const colors = getShiftColors(shift.shiftType)
        events.push({
          id: `${agent.userName}-${idx}`,
          text: shift.shiftType ? getLocalizedShiftType(t, shift.shiftType) : "",
          start: formatInServerTimeZone(shift.shiftStart, "yyyy-MM-dd'T'HH:mm:ss"),
          end: formatInServerTimeZone(shift.shiftEnd, "yyyy-MM-dd'T'HH:mm:ss"),
          resource: agent.userName,
          backColor: colors.background,
          fontColor: colors.text,
          barHidden: false,
        })
      }
    }

    return { agentMap, resources, events }
  }, [agents, users, t])

  const handleBeforeRowHeaderRender = useCallback(
    (args: DayPilot.SchedulerBeforeRowHeaderRenderArgs) => {
      const userName = args.row.id.toString()
      const agent = agentMap.get(userName)
      const user = users?.get(userName)
      if (agent && user) {
        const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
        const specialtiesText = getLocalizedSpecialties(t, agent.specialties)
        const fullName = joinStrings([user.firstName, user.lastName], " ")
        const subtitle = joinStrings([t(getRoleName(user.role)), specialtiesText])
        args.row.html = DOMPurify.sanitize(
          `<div class="agent-row">
          <div class="agent-avatar ${getAvatarColorByRole(user.role)}" data-username="${agent.userName}">${initials}</div>
          <div class="agent-info">
            <div class="agent-name">${fullName}</div>
            <div class="agent-specialties" data-testid="shift-plan-agent-specialties">${subtitle}</div>
          </div>
        </div>`,
        )
      }
    },
    [agentMap, users, t],
  )

  const handleBeforeTimeHeaderRender = (args: DayPilot.SchedulerBeforeTimeHeaderRenderArgs) => {
    const now = new DayPilot.Date(formatInServerTimeZone(new Date(), "yyyy-MM-dd'T'HH:mm:ss"))
    if (args.header.start <= now && args.header.end > now) {
      args.header.cssClass = "current-time-header"
    }
  }

  const handleBeforeCellRender = (args: DayPilot.SchedulerBeforeCellRenderArgs) => {
    const now = new DayPilot.Date(formatInServerTimeZone(new Date(), "yyyy-MM-dd'T'HH:mm:ss"))
    if (args.cell.start <= now && args.cell.end > now) {
      args.cell.properties.cssClass = "current-time-cell"
    }
  }

  const onScrollHandler = () => {
    const isToday = format(date, "yyyy-MM-dd") === formatInServerTimeZone(new Date(), "yyyy-MM-dd")
    if (!isToday) return

    const scrollEl = document.querySelector<HTMLElement>(".scheduler_default_scrollable")
    const currentTimeEl = document.querySelector<HTMLElement>(".current-time-header")
    if (!scrollEl || !currentTimeEl) return

    const scrollRect = scrollEl.getBoundingClientRect()
    const timeRect = currentTimeEl.getBoundingClientRect()
    const offsetInContent = timeRect.left - scrollRect.left + scrollEl.scrollLeft
    const scrollX = offsetInContent - timeRect.width

    scrollEl.scrollTo({ left: scrollX, behavior: "smooth" })
  }

  return (
    <AgentTooltipProvider agents={agentMap}>
      <div ref={wrapperRef} className="h-full w-full">
        <DayPilotScheduler
          startDate={format(date, "yyyy-MM-dd")}
          days={1}
          scale="CellDuration"
          cellDuration={30}
          timeHeaders={[{ groupBy: "Hour", format: "HH:mm" }]}
          eventMoveHandling="Disabled"
          eventResizeHandling="Disabled"
          eventDeleteHandling="Disabled"
          timeRangeSelectedHandling="Disabled"
          dynamicEventRendering="Disabled"
          headerHeight={36}
          rowHeaderWidth={260}
          eventHeight={24}
          rowMarginBottom={12}
          rowMarginTop={12}
          cellWidth={48}
          heightSpec="Max"
          height={schedulerHeight}
          durationBarVisible={false}
          resources={resources}
          events={events}
          onBeforeRowHeaderRender={handleBeforeRowHeaderRender}
          onBeforeTimeHeaderRender={handleBeforeTimeHeaderRender}
          onBeforeCellRender={handleBeforeCellRender}
          onAfterUpdate={onScrollHandler}
        />
      </div>
    </AgentTooltipProvider>
  )
}
